import React, { useRef, useEffect } from 'react';
import { Box } from '@mui/material';

interface DigitCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  onComplete?: () => void;
}

const boxStyle: React.CSSProperties = {
  width: 56,
  height: 56,
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  borderRadius: 10,
  border: '1px solid rgba(255, 255, 255, 0.1)',
  color: '#fff',
  fontSize: '1.4rem',
  fontWeight: 600,
  textAlign: 'center',
  outline: 'none',
  caretColor: '#354EEF',
};

const boxFilledStyle: React.CSSProperties = {
  ...boxStyle,
  borderColor: 'rgba(255, 255, 255, 0.25)',
};

const DigitCodeInput: React.FC<DigitCodeInputProps> = ({
  value,
  onChange,
  disabled = false,
  autoFocus = false,
  onComplete,
}) => {
  const digits = Array.from({ length: 6 }, (_, i) => value[i] ?? '');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const focusIndex = (i: number) => {
    if (i >= 0 && i < 6 && inputRefs.current[i]) {
      inputRefs.current[i]!.focus();
    }
  };

  const updateValue = (newDigits: string[]) => {
    const newValue = newDigits.join('').replace(/\D/g, '').slice(0, 6);
    onChange(newValue);
    if (newValue.length === 6 && onComplete) {
      setTimeout(onComplete, 0);
    }
  };

  const handleChange = (index: number, inputValue: string) => {
    const cleaned = inputValue.replace(/\D/g, '');
    if (!cleaned) return;

    if (cleaned.length > 1) {
      const newDigits = [...digits];
      for (let i = 0; i < cleaned.length && index + i < 6; i++) {
        newDigits[index + i] = cleaned[i];
      }
      updateValue(newDigits);
      focusIndex(Math.min(index + cleaned.length, 5));
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = cleaned;
    updateValue(newDigits);
    focusIndex(index + 1);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newDigits = [...digits];
      if (digits[index]) {
        newDigits[index] = '';
        updateValue(newDigits);
      } else if (index > 0) {
        newDigits[index - 1] = '';
        updateValue(newDigits);
        focusIndex(index - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      focusIndex(index - 1);
    } else if (e.key === 'ArrowRight') {
      focusIndex(index + 1);
    } else if (e.key === 'Enter' && value.length === 6 && onComplete) {
      onComplete();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted) {
      onChange(pasted);
      focusIndex(Math.min(pasted.length, 5));
      if (pasted.length === 6 && onComplete) {
        setTimeout(onComplete, 0);
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => { inputRefs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={2}
          value={digit}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          style={digit ? boxFilledStyle : boxStyle}
        />
      ))}
    </Box>
  );
};

export default DigitCodeInput;
